import { Dispatch, FC, SetStateAction, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useConfigure, useHitsPerPage, useRefinementList } from 'react-instantsearch'

import { getCountryAndLocaleStrings } from '../../utils/locales'

interface CatalogCountsProps {
  setCounts: Dispatch<SetStateAction<Record<string, number>>>
}

/**
 * This component should be used inside a separate instnatsearch index
 * to fetch category counts without any catalog filtering applied (to get full counts)
 * @param setCounts to pass the counts back to parent component
 * @constructor
 */
const CatalogCounts: FC<CatalogCountsProps> = ({ setCounts }) => {
  const { locale: countryAndLocale } = useRouter()
  const { locale, country } = getCountryAndLocaleStrings(countryAndLocale)
  const {
    items,
  } = useRefinementList({ attribute: `categories.one.${locale}`, operator: 'or', limit: 400 })

  const {
    items: level2Items,
  } = useRefinementList({ attribute: `categories.two.${locale}`, operator: 'or', limit: 400 })

  // These hooks are needed to prevent instantsearch from applying refinements from other index
  const {
    items: level3Items,
  } = useRefinementList({ attribute: `categories.three.${locale}`, operator: 'or', limit: 400 })
  // console.log(items, level2Items, level3Items)

  useConfigure({
    facetsRefinements: {
      [`categories.one.${locale}`]: [],
      [`categories.two.${locale}`]: [],
      [`categories.three.${locale}`]: [],
      'country-availability': [country.toUpperCase()],
    },
  })

  // No need to fetch any hits (but use 1 as 0 doesn't work),
  // as this component is only used for facet counts
  useHitsPerPage({ items: [{ label: 'No hits', value: 1, default: true }] })

  useEffect(() => {
    setCounts(Object.fromEntries(
      items.concat(level2Items).concat(level3Items).map((item) => [item.label, item.count]),
    ))
  }, [items, level2Items, level3Items])

  return null
}

export default CatalogCounts
